import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { DefaultFilledButton } from '@magoosh/lib/button';

import style from './style.module.scss';

interface Props {
  content: string;
  category: string;
  element: HTMLElement;
  index: number;
  onClose: () => void;
  onNext: () => void;
}

const AnnotationPopover: React.FC<Props> = (props) => {
  const annotationRef = useRef(null);
  const [currentPosition, setCurrentPosition] = useState(null);

  // On window resize, reposition active annotation if it exists
  useEffect(() => {
    // Scroll element into view if not visible
    const rect = props.element.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    const popoverHeight = annotationRef.current?.offsetHeight || 0;
    
    // On mobile, consider element not visible if covered by popover at bottom
    const effectiveViewportBottom = isMobile ? window.innerHeight - popoverHeight : window.innerHeight;
    
    const isVisible = rect.top >= 0 && 
                     rect.bottom <= effectiveViewportBottom && 
                     rect.left >= 0 && 
                     rect.right <= window.innerWidth;
    
    if (!isVisible) {
      props.element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
      });
    }

    setCurrentPosition(calculateAnnotationPosition());

    const handleResize = () => {
      setCurrentPosition(calculateAnnotationPosition());
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [props.index]);

  // When clicking elsewhere on the page, close the annotation popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!annotationRef?.current) return;

      if (!annotationRef.current.contains(event.target)) {
        props.onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calculateAnnotationPosition = () => {
    const rect = props.element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Get actual annotation dimensions
    const annotationHeight = annotationRef.current?.offsetHeight || 300;
    const annotationWidth = annotationRef.current?.offsetWidth || 375;
    
    // Check if annotation would be outside viewport when placed below
    const wouldBeOutsideBottom = rect.bottom + annotationHeight > viewportHeight;
    
    // Calculate vertical position
    const top = wouldBeOutsideBottom 
      ? rect.top + window.scrollY - annotationHeight - 65
      : rect.bottom + window.scrollY - 50;
    
    // Find the nearest ancestor with relative positioning
    let relativeAncestor = props.element.closest('[style*="relative"]') || 
                          props.element.closest('.position-relative') ||
                          document.body;
    
    const ancestorRect = relativeAncestor.getBoundingClientRect();
    const ancestorRight = ancestorRect.right;
    
    // Calculate horizontal position, ensuring it doesn't go off-screen to the right
    let left = rect.left + window.scrollX;
    if (left + annotationWidth > ancestorRight + window.scrollX) {
      left = ancestorRight + window.scrollX - annotationWidth - 20; // 20px from right edge
    }

    return {
      top,
      left
    };
  };

  if (!currentPosition) return null;

  return (
    <div
      className={cx(style.popover, 'd-flex flex-column gap-m')}
      ref={annotationRef}
      style={{
        left: currentPosition.left,
        top: currentPosition.top,
      }}
    >
      <div className="visible-xs">
        <div className="align-items-center d-flex">
          <div className="flex-1" />

          <div className="d-flex flex-1 justify-content-center">
            <div className="d-flex flex-column" style={{ gap: 2 }}>
              <div style={{ backgroundColor: '#4b504e', height: 2, width: 20 }} />
              <div style={{ backgroundColor: '#4b504e', height: 2, width: 20 }} />
              <div style={{ backgroundColor: '#4b504e', height: 2, width: 20 }} />
            </div>
          </div>

          <div className="d-flex flex-1 justify-content-end">
            <a className="text-body text-no-decoration text-hover-no-decoration" onClick={props.onClose}>
              <strong>Close</strong>
            </a>
          </div>
        </div>
      </div>

      <div className="text-bold">
        Suggested Edit{' '}
        <span
          style={{
            border: '1px solid #272125',
            borderRadius: '3px',
            padding: '1px 4px'
          }}
        >
          {props.index}
        </span>
      </div>
      
      <div className="bg-gray-lighter small" style={{ padding: '2px 4px' }}>
        <strong>Theme:</strong> {props.category}
      </div>
      
      <div className="flex-1">{props.content}</div>
      
      <div className="d-flex justify-content-end">
        <DefaultFilledButton onClick={props.onNext}>
          View Next
        </DefaultFilledButton>
      </div>
    </div>
  );
};

export default AnnotationPopover;
